/**
 * handoffRouting.ts — Centralized Hand-off Resolver
 *
 * Single source of truth for "which hand-offs should this viewer see?"
 * Used by RoleHandoffInbox, sidebar badge counters, and any future
 * escalation / analytics surface. NO dashboard should reimplement this.
 *
 * Routing priority (most specific → least):
 *   1. assignedToUserId === viewer.userId           (direct-person)
 *   2. assignedToRoleKey === viewer.role            (canonical role-seat)
 *   3. legacy alias map: assignedTo string → role   (backward compat)
 *
 * The legacy alias layer is INTENTIONALLY isolated to this file. It exists
 * only to absorb pre-canonical follow-up records (created before
 * assignedToRoleKey was written). Once seed data + in-flight tasks are
 * migrated, delete LEGACY_ASSIGNEE_TO_ROLE and the resolveLegacyRole call
 * in `matchesViewer`. Do NOT scatter alias logic anywhere else.
 */

import type { FollowUpTask } from "./opportunityTypes";
import type { UserRole } from "./RoleContext";

// ─────────────────────────────────────────────────────────────────────────────
// Viewer
// ─────────────────────────────────────────────────────────────────────────────

export interface HandoffViewer {
  /** Stable role key of the current user (e.g. "head_of_installation"). */
  role: UserRole;
  /** Optional user identifier — when present, direct-person assignments resolve. */
  userId?: string;
  /** Display label (e.g. "Head of Install"). Used only for new task creation defaults. */
  label?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY ALIAS LAYER — temporary, single-source. Remove after migration.
// ─────────────────────────────────────────────────────────────────────────────
// Maps free-form display labels (lowercased, trimmed) found on legacy
// FollowUpTask.assignedTo strings to canonical role keys.
const LEGACY_ASSIGNEE_TO_ROLE: Record<string, UserRole> = {
  // Install
  "head of install": "head_of_installation",
  "head of installation": "head_of_installation",
  "install lead": "head_of_installation",
  "install manager": "head_of_installation",
  "installation manager": "head_of_installation",
  // Procurement
  "procurement": "procurement_manager",
  "procurement manager": "procurement_manager",
  "purchasing": "procurement_manager",
  "buyer": "procurement_manager",
  // Inventory / Warehouse
  "inventory": "inventory_manager",
  "inventory manager": "inventory_manager",
  "warehouse": "warehouse_manager",
  "warehouse manager": "warehouse_manager",
  "warehouse lead": "warehouse_manager",
  // Ops
  "ops": "ops_manager",
  "ops manager": "ops_manager",
  "operations manager": "ops_manager",
  // Sales
  "sales": "sales",
  "sales rep": "sales",
  "sales manager": "sales_manager",
  // Finance
  "cfo": "cfo",
  "finance manager": "finance_manager",
  "bookkeeper": "bookkeeper",
  // Admin
  "admin": "admin",
  "owner": "owner",
  "super admin": "super_admin",
  "call center": "call_center",
  "field crew": "installer",
  "installer": "installer",
  "subcontractor": "subcontractor",
};

/**
 * Resolve a legacy display string to a canonical role key.
 * Returns null when no mapping exists (the task simply won't route by role).
 * @internal — only used by `matchesViewer`. Do not export the alias map.
 */
function resolveLegacyRole(assignedTo: string | undefined): UserRole | null {
  if (!assignedTo) return null;
  const key = assignedTo.toLowerCase().trim();
  return LEGACY_ASSIGNEE_TO_ROLE[key] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core matcher
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Does this task belong in the given viewer's inbox?
 * Pure, deterministic, side-effect-free.
 */
export function matchesViewer(task: FollowUpTask, viewer: HandoffViewer): boolean {
  // 1. Direct-person assignment wins.
  if (task.assignedToUserId && viewer.userId && task.assignedToUserId === viewer.userId) {
    return true;
  }
  // 2. Canonical role-seat assignment.
  if (task.assignedToRoleKey && task.assignedToRoleKey === viewer.role) {
    return true;
  }
  // 3. Legacy fallback (only when no canonical fields are set).
  if (!task.assignedToRoleKey && !task.assignedToUserId) {
    const legacyRole = resolveLegacyRole(task.assignedTo);
    if (legacyRole && legacyRole === viewer.role) return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public selectors — every consumer (inbox, badge, future escalation) MUST
// use these so visibility cannot drift.
// ─────────────────────────────────────────────────────────────────────────────

/** All open hand-offs visible to the viewer, sorted by due date ascending. */
export function getHandoffsForViewer(
  viewer: HandoffViewer,
  tasks: FollowUpTask[],
): FollowUpTask[] {
  return tasks
    .filter((t) => t.status !== "completed")
    .filter((t) => matchesViewer(t, viewer))
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
}

/** Count of open hand-offs visible to the viewer (for sidebar badges). */
export function getOpenHandoffCountForViewer(
  viewer: HandoffViewer,
  tasks: FollowUpTask[],
): number {
  return getHandoffsForViewer(viewer, tasks).length;
}

/** Convenience: hand-offs whose due date has passed. */
export function getOverdueHandoffsForViewer(
  viewer: HandoffViewer,
  tasks: FollowUpTask[],
): FollowUpTask[] {
  const now = Date.now();
  return getHandoffsForViewer(viewer, tasks).filter(
    (t) => new Date(t.dueDate).getTime() < now,
  );
}
